import en from './messages/en.json';
import no from './messages/no.json';
import type { TranslationKey } from './keys';

export const languages = ['en', 'no'] as const;
export type Language = (typeof languages)[number];

export const defaultLanguage: Language = 'en';

export const languageNames: Record<Language, string> = {
	en: 'English',
	no: 'Norsk'
};

const translations: Record<Language, Record<string, string>> = {
	en,
	no
};

/** Check whether a string is a valid supported language code. */
export function isValidLanguage(lang: string): lang is Language {
	return languages.includes(lang as Language);
}

/** Look up a translation by key for the given language, falling back to the default language. */
export function getTranslation(lang: Language, key: TranslationKey | (string & {})): string {
	const value = translations[lang]?.[key] ?? translations[defaultLanguage]?.[key];
	if (value === undefined && import.meta.env.DEV) {
		console.warn(`[i18n] Missing translation key: "${key}" for language "${lang}"`);
	}
	return value ?? key;
}

/** Return a translation function bound to the given language. */
export function t(lang: Language) {
	return (key: TranslationKey | (string & {})): string => getTranslation(lang, key);
}

/** Build a localized URL path for the given language and path. */
export function localizedUrl(lang: Language, path: string): string {
	// Remove leading slash if present
	const cleanPath = path.startsWith('/') ? path.slice(1) : path;
	// Remove any existing language prefix
	const pathWithoutLang = cleanPath.replace(/^(en|no)\//, '').replace(/^(en|no)$/, '');
	// Add the language prefix
	return `/${lang}${pathWithoutLang ? `/${pathWithoutLang}` : ''}`;
}

/** Switch the language prefix in a URL path, preserving the rest of the path. */
export function switchLanguageUrl(currentPath: string, newLang: Language): string {
	// Extract path after language prefix
	const match = currentPath.match(/^\/(en|no)(\/.*)?$/);
	if (match) {
		const restOfPath = match[2] || '';
		return `/${newLang}${restOfPath}`;
	}
	// If no language prefix, just add the new language
	return `/${newLang}${currentPath === '/' ? '' : currentPath}`;
}

/** Extract the language from a URL, falling back to the default language. */
export function getLanguageFromUrl(url: URL): Language {
	const pathSegments = url.pathname.split('/').filter(Boolean);
	const firstSegment = pathSegments[0];
	if (firstSegment && isValidLanguage(firstSegment)) {
		return firstSegment;
	}
	return defaultLanguage;
}
